import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { LanguageService } from '../services/language/language.service';
import {
  NEXT_TAB,
  PREVIOUS_TAB,
} from 'src/app/modules/route-planner/constants/multi-language.constants';

/**
 * Patches accessibility on Angular Material's mat-tab-group pagination
 * chevrons (the <,> arrows shown when there are more tabs than fit).
 *
 * Why a directive:
 * Material renders those buttons internally with `aria-hidden="true"` and
 * `tabindex="-1"` because the official APG tab pattern uses Arrow keys on
 * the tab list itself for navigation. The a11y audit (mobile carousel)
 * explicitly required these chevrons to be reachable, declared as buttons
 * with accessible names, and to expose their disabled state. We therefore
 * un-hide them, set localized aria-labels, make them tab-stops, and mirror
 * Material's "...-disabled" class onto `aria-disabled` so SR users hear
 * "dimmed" / "unavailable" at the end of the scroll range.
 *
 * Scope:
 * Attribute selector `mat-tab-group` so it auto-applies to every tab strip
 * in the app (route planner main tabs, results detail tabs, etc.) without
 * touching individual templates. Registered + exported from SharedModule.
 */
@Directive({
  selector: 'mat-tab-group',
  standalone: false,
})
export class MatTabPaginationA11yDirective implements OnInit, OnDestroy {
  private static readonly BEFORE_SEL = '.mat-mdc-tab-header-pagination-before';
  private static readonly AFTER_SEL = '.mat-mdc-tab-header-pagination-after';
  private static readonly DISABLED_CLASS =
    'mat-mdc-tab-header-pagination-disabled';

  private readonly observers: MutationObserver[] = [];
  private readonly buttons: HTMLElement[] = [];
  private languageSub?: Subscription;
  /** Watches the tab header for the pagination buttons appearing/disappearing. */
  private headerObserver?: MutationObserver;
  private previousLabel = PREVIOUS_TAB.hebrew;
  private nextLabel = NEXT_TAB.hebrew;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Optional() private readonly languageService?: LanguageService
  ) {}

  ngOnInit(): void {
    this.subscribeLanguage();
    // Defer to next macrotask so Material has rendered the inner header
    // (including the pagination button shells) on first change detection.
    this.zone.runOutsideAngular(() => {
      setTimeout(() => this.attachPaginationButtons());
    });
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.headerObserver?.disconnect();
    this.languageSub?.unsubscribe();
  }

  private subscribeLanguage(): void {
    if (!this.languageService) {
      return;
    }
    this.languageSub = this.languageService.selectedLanguage$.subscribe(
      (language) => {
        const key = (language?.language ?? 'hebrew') as keyof typeof PREVIOUS_TAB;
        this.previousLabel = PREVIOUS_TAB[key] ?? PREVIOUS_TAB.hebrew;
        this.nextLabel = NEXT_TAB[key] ?? NEXT_TAB.hebrew;
        this.refreshLabels();
      }
    );
  }

  private attachPaginationButtons(): void {
    const root = this.host.nativeElement;
    const before = root.querySelector<HTMLElement>(
      MatTabPaginationA11yDirective.BEFORE_SEL
    );
    const after = root.querySelector<HTMLElement>(
      MatTabPaginationA11yDirective.AFTER_SEL
    );

    if (before) {
      this.configure(before, () => this.previousLabel);
    }
    if (after) {
      this.configure(after, () => this.nextLabel);
    }

    // Material can render the buttons asynchronously (e.g. when overflow
    // first appears after a resize). Watch the tab header subtree once so
    // late-rendered buttons are still configured.
    if (!before || !after) {
      const header = root.querySelector('.mat-mdc-tab-header');
      if (header) {
        this.headerObserver = new MutationObserver(() => {
          const b = root.querySelector<HTMLElement>(
            MatTabPaginationA11yDirective.BEFORE_SEL
          );
          const a = root.querySelector<HTMLElement>(
            MatTabPaginationA11yDirective.AFTER_SEL
          );
          if (b && !this.buttons.includes(b)) {
            this.configure(b, () => this.previousLabel);
          }
          if (a && !this.buttons.includes(a)) {
            this.configure(a, () => this.nextLabel);
          }
          if (b && a) {
            this.headerObserver?.disconnect();
            this.headerObserver = undefined;
          }
        });
        this.headerObserver.observe(header, {
          childList: true,
          subtree: true,
        });
      }
    }
  }

  private configure(el: HTMLElement, label: () => string): void {
    el.removeAttribute('aria-hidden');
    el.setAttribute('role', 'button');
    el.setAttribute('aria-label', label());
    el.setAttribute('tabindex', '0');
    // Initial disabled state.
    this.syncDisabled(el);
    // Watch for Material toggling the "...-disabled" class so aria-disabled
    // stays in sync (audit requirement: announce when the chevron is at
    // the end of the scroll range and can't go further).
    const obs = new MutationObserver(() => this.syncDisabled(el));
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    this.observers.push(obs);
    this.buttons.push(el);
  }

  private syncDisabled(el: HTMLElement): void {
    const isDisabled = el.classList.contains(
      MatTabPaginationA11yDirective.DISABLED_CLASS
    );
    el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
  }

  private refreshLabels(): void {
    const root = this.host.nativeElement;
    const before = root.querySelector<HTMLElement>(
      MatTabPaginationA11yDirective.BEFORE_SEL
    );
    const after = root.querySelector<HTMLElement>(
      MatTabPaginationA11yDirective.AFTER_SEL
    );
    if (before) {
      before.setAttribute('aria-label', this.previousLabel);
    }
    if (after) {
      after.setAttribute('aria-label', this.nextLabel);
    }
  }
}
